import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getTermBySlug, glossarioTerms } from '../data/glossario';
import { guias } from '../data/guias';
import { NotFoundPage } from './NotFoundPage';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const GlossarioTermPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const termo = slug ? getTermBySlug(slug) : undefined;

  if (!termo) return <NotFoundPage />;

  const canonicalUrl = `${SITE_URL}/glossario/${termo.slug}`;
  const saibaMaisTermos = termo.saibaMais
    ? glossarioTerms.filter((t) => termo.saibaMais!.includes(t.slug))
    : [];

  const relatedGuides = guias.filter((g) => g.glossario?.includes(termo.slug));

  const jsonLd: any[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'DefinedTerm',
      name: termo.termo,
      description: termo.resumo,
      url: canonicalUrl,
      inDefinedTermSet: `${SITE_URL}/glossario`,
      inLanguage: 'pt-BR',
    },
  ];
  if (termo.faq && termo.faq.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: termo.faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  // Convert markdown-style **bold** to <strong> in definicao
  const formatDefinicao = (text: string) =>
    text
      .split('\n\n')
      .map((paragraph) =>
        paragraph
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\n/g, '<br />')
      );

  const paragraphs = formatDefinicao(termo.definicao);

  return (
    <>
      <Helmet>
        <title>{termo.termo}: O que é? Significado e Definição | {SITE_NAME}</title>
        <meta name="description" content={`${termo.resumo} Definição completa com exemplos práticos para o investidor brasileiro.`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${termo.termo} — O que é?`} />
        <meta property="og:description" content={termo.resumo} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/glossario" className="hover:text-blue-600 transition">Glossário</Link>
            <span>›</span>
            <span className="text-gray-700 font-medium">{termo.termo}</span>
          </nav>

          {/* Main Card */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-6">
            {/* Category badge */}
            <span className="inline-block text-xs font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-full mb-4">
              {termo.categoria}
            </span>

            <h1 className="text-3xl font-bold text-gray-900 mb-3">{termo.termo}</h1>

            {/* Summary */}
            <p className="text-lg text-gray-600 leading-relaxed border-l-4 border-blue-500 pl-4 mb-6">
              {termo.resumo}
            </p>

            {/* Full definition */}
            <div className="prose max-w-none text-gray-700 leading-relaxed space-y-4">
              {paragraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          </div>

          {/* Practical Example */}
          {termo.exemploPratico && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">💡</span>
                <h2 className="font-bold text-yellow-800 text-lg">Exemplo Prático</h2>
              </div>
              <p className="text-yellow-900 leading-relaxed">{termo.exemploPratico}</p>
            </div>
          )}

          {/* See Also */}
          {saibaMaisTermos.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="font-bold text-gray-900 text-lg mb-4">Veja também</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {saibaMaisTermos.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/glossario/${t.slug}`}
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition group"
                  >
                    <div>
                      <div className="font-semibold text-gray-800 group-hover:text-blue-600 transition text-sm">
                        {t.termo}
                      </div>
                      <div className="text-gray-500 text-xs mt-0.5 line-clamp-2">{t.resumo}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* FAQ */}
          {termo.faq && termo.faq.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="font-bold text-gray-900 text-lg mb-4">Perguntas frequentes</h2>
              <div className="space-y-5">
                {termo.faq.map((faq, index) => (
                  <div key={index}>
                    <h3 className="font-semibold text-gray-900 mb-1">{faq.question}</h3>
                    <p className="text-gray-600 text-[15px] leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Guias relacionados */}
          {relatedGuides.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="font-bold text-gray-900 text-lg mb-4">Guias relacionados</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedGuides.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/aprenda/${g.slug}`}
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition group"
                  >
                    <div>
                      <div className="font-semibold text-gray-800 group-hover:text-blue-600 transition text-sm">
                        {g.title}
                      </div>
                      <div className="text-gray-500 text-xs mt-0.5 line-clamp-2">{g.description}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Notícias relacionadas */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-6 flex items-center justify-between gap-4">
            <p className="text-blue-900 text-sm font-medium">
              Acompanhe as notícias mais recentes sobre este tema.
            </p>
            <Link
              to={`/?busca=${encodeURIComponent(termo.termo.split(' ')[0])}`}
              className="flex-shrink-0 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Ver notícias
            </Link>
          </div>

          {/* Back to glossary */}
          <div className="text-center">
            <Link
              to="/glossario"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition"
            >
              ← Voltar ao Glossário completo
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
